import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized } from 'vue-router'
import { workspaceForPath } from '@/constants/workspaces'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import RouteViewComponent from '../layouts/RouterBypass.vue'
import { aiAgentsLevel } from '../utils/aiAgentsLabels'
import { ALL_SUPPORT_LEVELS } from '../utils/supportAccess'
import { useUserStore } from '../stores/user-store'
import store from '../stores'

// Caminhos do CRM anteriores ao prefixo /crm. Mantidos vivos por redirect.
const CRM_LEGACY_PATHS = [
  '/brands',
  '/companies',
  '/map',
  '/indicators',
  '/coordinates',
  '/company-localities/:id',
  '/client-modules/:id&:name',
  '/modules',
  '/pilots',
  '/data_integration_types',
  '/database_names',
  '/localities',
  '/locality_types',
  '/sort-types',
  '/palletization_types',
  '/audits',
  '/notifications',
  '/users',
  '/contacts',
]

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'home' },
  },
  // Compatibilidade: os caminhos antigos continuam respondendo. Há links
  // destes colados no Slack e nos favoritos do time — renomear a URL sem
  // redirecionar transformaria cada um deles num 404 silencioso.
  { path: '/inicio', redirect: { name: 'home' } },
  // O CRM ganhou o prefixo /crm (ver o bloco "Workspace: Easy CRM" abaixo).
  // Estes são os caminhos de quando ele morava na raiz. Sem o redirect, cada
  // link colado no Slack e cada favorito do time vira um 404 silencioso — e o
  // volume aqui é grande, são as telas do dia a dia.
  //
  // O redirect reescreve só o prefixo, então rota com parâmetro (/company-
  // localities/5) e querystring (?page=2) chegam inteiras do outro lado.
  ...CRM_LEGACY_PATHS.map((legado) => ({
    path: legado,
    redirect: (to: RouteLocationNormalized) => ({
      path: `/crm${to.path}`,
      query: to.query,
      hash: to.hash,
    }),
  })),
  { path: '/registration', redirect: { name: 'registration' } },
  { path: '/deployments', redirect: { name: 'project-gestao' } },
  { path: '/service-desk/implantacoes', redirect: { name: 'project-implantacoes' } },
  {
    path: '/service-desk/implantacoes/:id',
    redirect: (to) => ({ name: 'project-implantacao', params: to.params }),
  },
  { path: '/service-desk/projetos', redirect: { name: 'project-projetos' } },
  { path: '/service-desk/projetos/:id', redirect: (to) => ({ name: 'project-projeto', params: to.params }) },
  // Implantações e Projetos saíram do Service Desk e viraram o Easy Project.
  // Estes são os caminhos que estavam NO AR até agora — há link deles no Slack
  // e nos favoritos do time, e sem o redirect viram 404 silencioso.
  { path: '/service-desk/deployment-management', redirect: { name: 'project-gestao' } },
  { path: '/service-desk/deployments', redirect: { name: 'project-implantacoes' } },
  {
    path: '/service-desk/deployments/:id',
    redirect: (to) => ({ name: 'project-implantacao', params: to.params, query: to.query, hash: to.hash }),
  },
  { path: '/service-desk/projects', redirect: { name: 'project-projetos' } },
  {
    path: '/service-desk/projects/:id',
    redirect: (to) => ({ name: 'project-projeto', params: to.params, query: to.query, hash: to.hash }),
  },
  { path: '/service-desk/escala', redirect: { name: 'sd-escala' } },
  { path: '/service-desk/atendimentos', redirect: { name: 'sd-atendimentos' } },
  { path: '/service-desk/notificacoes-slack', redirect: { name: 'sd-slack-auditoria' } },
  { path: '/service-desk/tickets/novo', redirect: { name: 'sd-ticket-new' } },

  // Antessala (tela central do HUB): fora do AppLayout — sem navbar, sem sidebar.
  {
    name: 'home',
    path: '/hub',
    component: () => import('../pages/home/HomePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'home' },
    children: [
      {
        name: 'monitoring',
        path: 'monitoring',
        component: () => import('../pages/monitoring/MonitoringPage.vue'),
        meta: { requiresAuth: true, flatBackground: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },

      // ──────────────── Workspace: AI Agents (interno) ────────────────
      {
        name: 'ai-agents',
        path: 'ai-agents',
        component: RouteViewComponent,
        redirect: { name: 'ai-home' },
        meta: { requiresAuth: true, requiresAiAgents: 'viewer' },
        children: [
          {
            name: 'ai-home',
            path: '',
            component: () => import('../pages/ai-agents/AiAgentsHomePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'ai-feedbacks',
            path: 'feedbacks',
            component: () => import('../pages/ai-agents/FeedbacksListPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'ai-review',
            path: 'review',
            component: () => import('../pages/ai-agents/FeedbacksListPage.vue'),
            props: { reviewQueue: true },
            meta: { requiresAuth: true, requiresAiAgents: 'reviewer' },
          },
          {
            name: 'ai-settings',
            path: 'settings',
            component: () => import('../pages/ai-agents/AiAgentsSettingsPage.vue'),
            meta: { requiresAuth: true, requiresAiAgents: 'admin' },
          },
          {
            name: 'ai-feedback',
            path: 'feedbacks/:id',
            component: () => import('../pages/ai-agents/FeedbackDetailPage.vue'),
            meta: { requiresAuth: true, breadcrumbParent: 'ai-feedbacks', breadcrumbLabel: 'Detalhe' },
          },
        ],
      },

      // ──────────────── Workspace: Easy Project ────────────────
      // Tocar projeto e implantação saiu do Service Desk e virou ferramenta
      // própria; lá ficou o que é atendimento (ticket, SLA, escala, plantão).
      {
        name: 'project',
        path: 'project',
        component: RouteViewComponent,
        redirect: { name: 'project-home' },
        meta: { requiresAuth: true },
        children: [
          {
            name: 'project-home',
            path: '',
            component: () => import('../pages/project/ProjectHomePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'project-gestao',
            path: 'deployment-management',
            component: () => import('../pages/implantacoes/ImplantacoesPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'project-implantacoes',
            path: 'deployments',
            component: () => import('../pages/service-desk/implantacoes/ImplantacoesListPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'project-implantacao',
            path: 'deployments/:id',
            component: () => import('../pages/service-desk/implantacoes/DeploymentDetailPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'project-projetos',
            path: 'projects',
            component: () => import('../pages/service-desk/projetos/ProjectsListPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'project-projeto',
            path: 'projects/:id',
            component: () => import('../pages/service-desk/projetos/ProjectBoardPage.vue'),
            meta: { requiresAuth: true, stableKey: true },
          },
        ],
      },

      // ──────────────── Workspace: Easy Service Desk ────────────────
      {
        name: 'service-desk',
        path: 'service-desk',
        component: RouteViewComponent,
        redirect: { name: 'sd-home' },
        meta: { requiresAuth: true },
        children: [
          {
            name: 'sd-home',
            path: '',
            component: () => import('../pages/service-desk/DashboardPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'sd-sla',
            path: 'sla',
            component: () => import('../pages/service-desk/SlaCentralPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'sd-tickets',
            path: 'tickets',
            component: () => import('../pages/service-desk/tickets/TicketsListPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'sd-ticket-new',
            path: 'tickets/new',
            component: () => import('../pages/service-desk/tickets/TicketForm.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'sd-ticket',
            path: 'tickets/:id',
            component: () => import('../pages/service-desk/tickets/TicketDetailPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'sd-escala',
            path: 'roster',
            component: () => import('../pages/service-desk/plantao/EscalaPage.vue'),
            meta: { requiresAuth: true, requiresSupport: ALL_SUPPORT_LEVELS },
          },
          {
            name: 'sd-atendimentos',
            path: 'shifts',
            component: () => import('../pages/service-desk/plantao/AtendimentosPage.vue'),
            meta: { requiresAuth: true, requiresSupport: ['n2', 'n3'] },
          },
          {
            name: 'sd-plantao-config',
            path: 'roster-settings',
            component: () => import('../pages/service-desk/plantao/ConfiguracaoPlantaoPage.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          {
            name: 'sd-slack-auditoria',
            path: 'slack-notifications',
            component: () => import('../pages/service-desk/plantao/AuditoriaSlackPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },

      // ──────────────── Workspace: Easy CRM ────────────────
      // O CRM era a única ferramenta sem prefixo: morava na raiz enquanto
      // Service Desk, Monitoring e AI Agents já tinham o seu. Com o domínio
      // virando hub.easypallet.app, as quatro ficam simétricas debaixo do HUB:
      // /crm/..., /service-desk/..., /monitoring, /ai-agents/...
      //
      // Preferências e Monitoramento NÃO entram aqui de propósito: Preferências
      // aparece no rodapé da sidebar de todas as áreas, e Monitoramento é
      // ferramenta própria (Easy Monitoring), não tela do CRM.
      {
        name: 'crm',
        path: 'crm',
        component: RouteViewComponent,
        redirect: { name: 'localities' },
        meta: { requiresAuth: true },
        children: [
          // {
          //   name: 'dashboard',
          //   path: 'dashboard',
          //   component: () => import('../pages/admin/dashboard/Dashboard.vue'),
          //   meta: { requiresAuth: true },
          // },
          {
            name: 'brands',
            path: 'brands',
            component: () => import('../pages/brands/BrandsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'companies',
            path: 'companies',
            component: () => import('../pages/companies/CompaniesPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'map',
            path: 'map',
            component: () => import('../pages/map/MapComponent.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'indicators',
            path: 'indicators',
            component: () => import('../pages/indicators/IndicatorsComponent.vue'),
            meta: { requiresAuth: true, flatBackground: true },
          },
          {
            name: 'registration',
            path: 'coordinates',
            component: () => import('../pages/map/RegistrationTable.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'company-localities',
            path: 'company-localities/:id',
            component: () => import('../pages/company-localities/CompanyLocalities.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'client-modules',
            path: 'client-modules/:id&:name',
            component: () => import('../pages/client-modules/ClientModulesPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'modules',
            path: 'modules',
            component: () => import('../pages/modules/ModulesPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'pilots',
            path: 'pilots',
            component: () => import('../pages/pilots/PilotsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'data_integration_types',
            path: 'data_integration_types',
            component: () => import('../pages/data_integration_types/DataIntegrationTypes.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'database_names',
            path: 'database_names',
            component: () => import('../pages/database_names/DatabaseNames.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'localities',
            path: 'localities',
            component: () => import('../pages/localities/LocalitiesPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'locality_types',
            path: 'locality_types',
            component: () => import('../pages/locality_types/LocalityTypesPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'sort-types',
            path: 'sort-types',
            component: () => import('../pages/sort-types/SortTypes.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'palletization_types',
            path: 'palletization_types',
            component: () => import('../pages/palletization_types/PalletizationTypesPage.vue'),
            meta: { requiresAuth: true },
          },
          //{
          //  name: 'settings',
          //  path: 'settings',
          //  component: () => import('../pages/settings/Settings.vue'),
          //  meta: { requiresAuth: true },
          //},
          {
            name: 'audits',
            path: 'audits',
            component: () => import('../pages/audits/AuditsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'notifications',
            path: 'notifications',
            component: () => import('../pages/notifications/NotificationsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'users',
            path: 'users',
            component: () => import('../pages/users/UsersPage.vue'),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          {
            name: 'contacts',
            path: 'contacts',
            component: () => import('../pages/contacts/ContactsPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: 'session-expired',
    path: '/session-expired',
    component: () => import('../pages/auth/SessionExpired.vue'),
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin)
  // Vale o nível mais exigente entre a rota e os pais (ex.: /ai-agents pede viewer, /review pede reviewer).
  const requiresAiAgents = to.matched
    .map((record) => record.meta.requiresAiAgents as string | undefined)
    .filter(Boolean)
    .sort((a, b) => aiAgentsLevel(b) - aiAgentsLevel(a))[0]
  const requiresSupport = to.matched
    .map((record) => record.meta.requiresSupport as readonly string[] | undefined)
    .find(Boolean)
  const isAuthenticated = !!localStorage.getItem('authToken')

  if (requiresAuth && !isAuthenticated) {
    next({ name: 'login' })
  } else if (requiresAiAgents) {
    const userStore = useUserStore()
    if (!userStore.id) await userStore.fetchUserData()
    next(userStore.aiAgentsAtLeast(requiresAiAgents) ? undefined : { name: 'home' })
  } else if (requiresSupport) {
    const userStore = useUserStore()
    if (!userStore.id) await userStore.fetchUserData()
    next(userStore.hasSupportAccess(requiresSupport) ? undefined : { name: 'sd-home' })
  } else if (requiresAdmin) {
    const userStore = useUserStore()
    await userStore.fetchUserData()
    if (!userStore.isAdmin) {
      next({ name: '' })
    } else {
      next()
    }
  } else {
    next()
  }
})

// Mantém o workspace ativo em sincronia com a rota (deep links, reload, voltar/avançar).
// A antessala não pertence a nenhuma ferramenta: preserva a última escolhida.
// Rotas que não pertencem a área nenhuma: entram preservando a última
// escolhida. Preferências é de todas as áreas — quem abre estando no Service
// Desk tem que continuar no Service Desk ao sair.
const WORKSPACE_NEUTRAL_ROUTES = new Set(['home', 'preferences'])

router.afterEach((to) => {
  if (WORKSPACE_NEUTRAL_ROUTES.has(to.name as string)) return
  store.commit('setWorkspace', workspaceForPath(to.path))
})

export default router
